import React, { useEffect, useState } from "react";
import { Shield, X, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

/* ================================================================
   InlinePlatformNoticeModal — shared 25-section modal used by:
     - Signup gate (unauth: fetches public /api/legal/disclaimers)
     - Authed backfill (PlatformNoticeAcknowledgment wrapper)
   Renders every section with visible numbering (1. → 25.).
   ================================================================ */

const PUBLIC_URL = `${process.env.REACT_APP_BACKEND_URL}/api/legal/disclaimers`;

export default function InlinePlatformNoticeModal({
    open,
    onDismiss,
    onAcknowledge,
    subtitle = null,
    // Pass a prefetched notice to skip the fetch (authed wrapper does this).
    notice: preloadedNotice = null,
    dismissible = true,
}) {
    const [notice, setNotice] = useState(preloadedNotice);
    const [checked, setChecked] = useState(false);
    const [busy, setBusy]       = useState(false);

    useEffect(() => {
        if (!open) return;
        if (preloadedNotice) { setNotice(preloadedNotice); return; }
        axios.get(PUBLIC_URL)
            .then((r) => setNotice(r.data?.platform_notice || null))
            .catch(() => {});
    }, [open, preloadedNotice]);

    const doAck = async () => {
        if (!notice) return;
        setBusy(true);
        try {
            await onAcknowledge?.({
                version: notice.version,
                acknowledged_at: new Date().toISOString(),
            });
        } finally {
            setBusy(false);
            setChecked(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] grid place-items-center px-4 py-8"
                    style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)" }}
                    data-testid="platform-notice-ack-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="ack-title"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0,  scale: 1 }}
                        exit={{    opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.24 }}
                        className="atlas-card w-full max-w-[760px] p-7 relative flex flex-col"
                        style={{ maxHeight: "92vh" }}
                    >
                        {dismissible && (
                            <button
                                onClick={onDismiss}
                                data-testid="ack-dismiss"
                                aria-label="Dismiss"
                                className="absolute top-3 right-3 w-8 h-8 rounded-md grid place-items-center atlas-btn-ghost"
                            >
                                <X className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                        )}

                        {/* Header */}
                        <div className="flex items-start gap-3 pb-4 border-b" style={{ borderColor: "var(--atlas-border-strong)" }}>
                            <div
                                className="w-11 h-11 rounded-xl grid place-items-center flex-shrink-0"
                                style={{
                                    background: "var(--atlas-primary-muted)",
                                    border: "1px solid rgba(139,92,246,0.4)",
                                }}
                            >
                                <Shield className="w-5 h-5" style={{ color: "var(--atlas-primary-hover)" }} strokeWidth={2} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2
                                    id="ack-title"
                                    className="text-[22px] font-bold tracking-tight leading-tight"
                                    style={{ color: "var(--atlas-text-primary)", letterSpacing: "-0.015em" }}
                                    data-testid="ack-title"
                                >
                                    Nexus One Platform Notice
                                </h2>
                                {notice && (
                                    <div
                                        className="mt-1 text-[10.5px] uppercase tracking-[0.14em]"
                                        style={{ color: "var(--atlas-text-muted)" }}
                                        data-testid="ack-version"
                                    >
                                        Version {notice.version} · updated {notice.updated}
                                    </div>
                                )}
                                {subtitle && (
                                    <div
                                        className="mt-2 text-[13px] leading-relaxed"
                                        style={{ color: "var(--atlas-text-secondary)" }}
                                        data-testid="ack-subtitle"
                                    >
                                        {subtitle}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Body — full 25 sections with visible numbering */}
                        <div
                            className="flex-1 overflow-y-auto atlas-scrollbar pr-2 py-4 min-h-0"
                            data-testid="ack-notice-body"
                        >
                            {!notice ? (
                                <div className="py-12 text-center animate-pulse" style={{ color: "var(--atlas-text-muted)" }}>
                                    Loading the Platform Notice…
                                </div>
                            ) : (
                                <>
                                    <div className="atlas-text-gradient text-[18px] font-bold leading-snug mb-3">
                                        {notice.title}
                                    </div>
                                    <p
                                        className="text-[13.5px] leading-relaxed mb-5"
                                        style={{ color: "var(--atlas-text-secondary)" }}
                                        data-testid="ack-preamble"
                                    >
                                        {notice.preamble}
                                    </p>
                                    <div className="space-y-4" data-testid="ack-sections">
                                        {(notice.sections || []).map((s, i) => (
                                            <section key={i} data-testid={`ack-section-${i}`}>
                                                <h3
                                                    className="text-[13.5px] font-semibold"
                                                    style={{ color: "var(--atlas-text-primary)" }}
                                                >
                                                    {s.heading}
                                                </h3>
                                                <p
                                                    className="mt-1 text-[12.5px] leading-relaxed"
                                                    style={{ color: "var(--atlas-text-secondary)" }}
                                                >
                                                    {s.body}
                                                </p>
                                            </section>
                                        ))}
                                    </div>
                                    <div
                                        className="mt-6 pt-4 border-t text-center"
                                        style={{ borderColor: "var(--atlas-border-strong)" }}
                                    >
                                        <span
                                            className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] font-semibold"
                                            style={{ color: "var(--atlas-primary-hover)" }}
                                            data-testid="ack-section-count"
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
                                            {(notice.sections || []).length} SECTIONS · READ IN FULL ABOVE
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="pt-4 mt-2 border-t space-y-4" style={{ borderColor: "var(--atlas-border-strong)" }}>
                            <label
                                className="flex items-start gap-2 text-[13px] leading-relaxed cursor-pointer select-none"
                                style={{ color: "var(--atlas-text-secondary)" }}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => setChecked(e.target.checked)}
                                    data-testid="ack-checkbox"
                                    className="mt-0.5 h-4 w-4 rounded accent-[color:var(--atlas-primary)]"
                                />
                                <span>
                                    I have read and acknowledge the Nexus One Platform Notice above. Every
                                    Workforce Member output is AI-generated and requires my review before external use.
                                </span>
                            </label>

                            <div className="flex items-center justify-end gap-2">
                                {dismissible && (
                                    <button
                                        onClick={onDismiss}
                                        data-testid="ack-later"
                                        className="atlas-btn-ghost rounded-lg px-4 h-9 text-[13px] font-medium"
                                    >
                                        Later
                                    </button>
                                )}
                                <button
                                    onClick={doAck}
                                    disabled={!checked || busy || !notice}
                                    data-testid="ack-confirm"
                                    className="atlas-btn-primary rounded-lg px-5 h-9 text-[13px] font-semibold disabled:opacity-50"
                                >
                                    {busy ? "Recording…" : "I Acknowledge"}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
